import { useElementSize, useMergedRef } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { defaultDragHandlePredicate, defaultOptions, disabledDragPredicate } from './defaults';
import css from './DragAndDrop.module.scss';
import { dragAndDropItemClass } from './Item';
import MuuriGrid, { DraggerEndEvent, GridOptions, Item } from './muuri';

type DragAndDropItems<T> = Array<{ muuriItem: Item; item: T }>;

export type DragAndDropOptions = Omit<
  GridOptions,
  | 'items'
  | 'dragEnabled'
  | 'itemClass'
  | 'itemVisibleClass'
  | 'itemDraggingClass'
  | 'itemReleasingClass'
  | 'itemHiddenClass'
  | 'layoutOnInit'
>;
export interface DragAndDropProps<T> {
  gridName: string;
  children: (item: T) => React.ReactNode;
  items: T[];
  gridOptions?: DragAndDropOptions;
  onDragEnd?: (movedItem: T, updatedItems: T[]) => void;
  onLayoutEnd?: (updatedItems: T[]) => void;
  onGridInit?: (gridName: string, grid: MuuriGrid) => void;
  disabled?: boolean;
  className?: string;
  testId?: string;
  /** muuri causes some issues when bundled in certain places, so we inject it here */
  Muuri: typeof MuuriGrid;
}
export function DragAndDrop<T>({
  gridName,
  gridOptions,
  items,
  children,
  onDragEnd,
  onLayoutEnd,
  onGridInit,
  className,
  disabled = false,
  testId = 'drag-and-drop',
  Muuri,
}: DragAndDropProps<T>): React.ReactElement {
  const muuriGrid = useRef<MuuriGrid>();
  const { ref, elementSize } = useElementSize();
  const gridRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergedRef(gridRef, ref);

  const itemMap = useRef<DragAndDropItems<T>>([]);
  useEffect(() => {
    if (gridRef.current) {
      // @ts-ignore
      const grid = new Muuri(gridRef.current, {
        ...defaultOptions(Muuri.AutoScroller),
        ...gridOptions,
        dragStartPredicate: disabled
          ? disabledDragPredicate
          : defaultDragHandlePredicate(Muuri.ItemDrag),
        items: [],
        dragContainer: gridRef.current,
      });

      muuriGrid.current = grid;
    }

    return () => {
      muuriGrid.current?.destroy();
      muuriGrid.current = undefined;
    };
  }, [gridOptions, disabled]);

  // gridInit
  useEffect(() => {
    if (muuriGrid.current) {
      onGridInit?.(gridName, muuriGrid.current);
    }
  }, [gridName, onGridInit]);

  // layoutEnd
  useEffect(() => {
    const onLayoutEndHandler = () => {
      onLayoutEnd?.(itemMap.current.map(i => i.item));
    };

    muuriGrid.current?.on('layoutEnd', onLayoutEndHandler);
    return () => {
      muuriGrid.current?.off('layoutEnd', onLayoutEndHandler);
    };
  }, [onLayoutEnd]);

  // dragEnd
  useEffect(() => {
    const onDragHandler = (item: Item, _: DraggerEndEvent) => {
      const muuriItems = muuriGrid.current?.getItems() ?? [];
      const newMap: DragAndDropItems<T> = [];
      const movedItem = itemMap.current.find(i => i.muuriItem === item);
      if (movedItem) {
        for (const muuriItem of muuriItems) {
          const foundItem = itemMap.current.find(i => i.muuriItem === muuriItem);
          if (foundItem) {
            newMap.push({ muuriItem, item: foundItem.item });
          }
        }
        itemMap.current = newMap;

        onDragEnd?.(
          movedItem?.item,
          newMap.map(m => m.item),
        );
      }
    };
    muuriGrid.current?.on('dragEnd', onDragHandler);
    return () => {
      muuriGrid.current?.off('dragEnd', onDragHandler);
    };
  }, [onDragEnd]);

  /**
   * 1. An item is added.
   *    This case is the easiest. We can simply query for that are not registered,
   *    and add them into the grid. We can batch show/refresh the items at the end
   *    of the useEffect
   *
   * 2. An item has been moved in the array
   *    When an item is moved in an array, Muuri doesn't care. We need to find the
   *    old index by traversing our T, Item map and using the move command to place
   *    it in the appropriate spot of the grid.
   *
   * 3. An item has been removed.
   *    Similar to #1 - Muuri doesn't know we actually destroyed an item. We can
   *    remove it by checking the internal mapping we keep of T and Item. If T no
   *    longer exists, then we must remove the muuri item.
   */
  useEffect(() => {
    if (muuriGrid.current && gridRef.current) {
      let currentMuuriItems = muuriGrid.current.getItems();
      const dragItemElements = gridRef.current.querySelectorAll(`.${dragAndDropItemClass}`);

      let newMuuriItems: Item[] = [];
      if (dragItemElements.length > 0) {
        const elementsNotTrackedByMuuri: HTMLElement[] = [];
        for (let i = 0; i < dragItemElements.length; i++) {
          const foundIndex = currentMuuriItems.findIndex(
            e => e.getElement() === dragItemElements[i],
          );
          if (foundIndex < 0) {
            elementsNotTrackedByMuuri.push(dragItemElements[i] as HTMLElement);
          }
        }
        newMuuriItems = muuriGrid.current.add(elementsNotTrackedByMuuri, {
          active: false,
          layout: false,
        });
      }

      currentMuuriItems = muuriGrid.current.getItems();
      for (const currentMuuriItem of currentMuuriItems) {
        const muuriElement = currentMuuriItem.getElement();
        const foundIndex = Array.from(dragItemElements).findIndex(e => e === muuriElement);
        if (foundIndex === -1) {
          muuriGrid.current.remove([currentMuuriItem], { layout: false });
        } else {
          muuriGrid.current.move(currentMuuriItem, foundIndex, { layout: false });
        }
      }

      // update the internal mapping and 'show' any new items
      currentMuuriItems = muuriGrid.current.getItems();
      const newMap: DragAndDropItems<T> = [];
      for (let i = 0; i < currentMuuriItems.length; i++) {
        newMap.push({ muuriItem: currentMuuriItems[i]!, item: items[i]! });
      }

      itemMap.current = newMap;
      if (newMuuriItems.length > 0) {
        muuriGrid.current.show(newMuuriItems);
      }

      // Call refreshItems here to sync any underlying dom sizing changes (eg. home cards)
      muuriGrid.current?.refreshItems().layout();
    }
  }, [items, gridOptions, disabled]);

  useEffect(() => {
    muuriGrid.current?.refreshItems().layout();
  }, [children]);

  useLayoutEffect(() => {
    if (elementSize?.width) {
      muuriGrid.current?.refreshItems().layout();
    }
  }, [elementSize?.width]);

  return (
    <div
      data-component="DragAndDrop"
      className={classNames(gridName, css.grid, { [css.draggable]: !disabled }, className)}
      ref={mergedRef}
      data-testid={testId}
    >
      {items.map(item => children(item))}
    </div>
  );
}
DragAndDrop.displayName = 'DragAndDrop';
